import { Input, Button, ErrorNotice } from '@kiipu/ui';
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CaretDown,
  Check,
  FolderSimple,
  MagnifyingGlass,
  Plus,
  Tag as TagIcon,
  X,
} from '@phosphor-icons/react';

import { api, type Folder, type Tag } from '../api';
import { folderPath } from './organization-helpers';
import './organization.css';

function moveChoice(event: KeyboardEvent<HTMLElement>) {
  if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
  if (
    event.target instanceof HTMLInputElement &&
    event.target.type === 'search' &&
    ['Home', 'End', 'ArrowUp'].includes(event.key)
  )
    return;
  const choices = [
    ...event.currentTarget.querySelectorAll<HTMLElement>('[data-picker-choice]:not(:disabled)'),
  ];
  if (!choices.length) return;
  const index = choices.indexOf(document.activeElement as HTMLElement);
  const next =
    event.key === 'Home'
      ? 0
      : event.key === 'End'
        ? choices.length - 1
        : event.key === 'ArrowDown'
          ? Math.min(index + 1, choices.length - 1)
          : Math.max(index - 1, 0);
  event.preventDefault();
  choices[next]?.focus();
}

function mergeCreated<T extends { id: string }>(entries: T[], created: T[]): T[] {
  return [...entries, ...created.filter((entry) => !entries.some(({ id }) => id === entry.id))];
}

export function FolderPicker({
  folders,
  value,
  onChange,
  label = '文件夹',
  rootLabel = '未整理',
  keepLabel,
  disabled = false,
  allowCreate = true,
  excludeIds,
  onBusyChange,
}: {
  folders: Folder[];
  value: string;
  onChange: (id: string) => void;
  label?: string;
  rootLabel?: string;
  keepLabel?: string;
  disabled?: boolean;
  allowCreate?: boolean;
  excludeIds?: Set<string>;
  onBusyChange?: (busy: boolean) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState('');
  const [created, setCreated] = useState<Folder[]>([]);
  const trigger = useRef<HTMLButtonElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const focusCreated = useRef(false);
  const id = useId();
  const client = useQueryClient();
  const all = mergeCreated(folders, created);
  const choices = all
    .filter((entry) => !excludeIds?.has(entry.id))
    .map((entry) => ({
      ...entry,
      path: folderPath(entry, all),
    }))
    .sort((a, b) => a.path.localeCompare(b.path, 'zh-CN'));
  const filtered = choices.filter((entry) =>
    entry.path.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()),
  );
  const selected = choices.find((entry) => entry.id === value);
  const close = () => {
    setExpanded(false);
    trigger.current?.focus();
  };
  const create = useMutation({
    mutationFn: () => api.createFolder(query.trim()),
    onSuccess: (folder) => {
      setCreated((entries) => [...entries, folder]);
      client.setQueryData<{ items: Folder[] }>(['folders'], (previous) => ({
        items: mergeCreated(previous?.items || folders, [folder]),
      }));
      void client.invalidateQueries({ queryKey: ['folders'] });
      onChange(folder.id);
      setQuery('');
      focusCreated.current = true;
      close();
    },
  });
  useEffect(() => {
    if (expanded) search.current?.focus();
  }, [expanded]);
  useEffect(() => {
    if (!create.isPending && focusCreated.current) {
      focusCreated.current = false;
      trigger.current?.focus();
    }
  }, [create.isPending]);
  useEffect(() => {
    onBusyChange?.(create.isPending);
  }, [create.isPending, onBusyChange]);
  return (
    <div className="organization-picker">
      <span className="field-label" id={`${id}-label`}>
        {label}
      </span>
      <button
        ref={trigger}
        type="button"
        className="organization-trigger"
        disabled={disabled || create.isPending}
        aria-labelledby={`${id}-label ${id}-value`}
        aria-expanded={expanded}
        aria-controls={`${id}-panel`}
        onClick={() => setExpanded((previous) => !previous)}
      >
        <FolderSimple size={16} aria-hidden="true" />
        <span id={`${id}-value`}>
          {value === '__keep' ? keepLabel : selected?.path || rootLabel}
        </span>
        <CaretDown size={14} aria-hidden="true" />
      </button>
      {expanded ? (
        <div
          className="organization-panel"
          id={`${id}-panel`}
          aria-labelledby={`${id}-label`}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              event.stopPropagation();
              close();
            } else moveChoice(event);
          }}
        >
          <div className="organization-search">
            <MagnifyingGlass size={15} aria-hidden="true" />
            <Input
              ref={search}
              type="search"
              aria-label="搜索文件夹名称或路径"
              placeholder="搜索文件夹…"
              value={query}
              disabled={disabled || create.isPending}
              onChange={(event) => {
                setQuery(event.target.value);
                create.reset();
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') event.preventDefault();
              }}
            />
          </div>
          <div className="organization-options">
            {[
              ...(keepLabel ? [{ id: '__keep', path: keepLabel }] : []),
              { id: '', path: rootLabel },
              ...filtered,
            ].map((entry) => (
              <button
                data-picker-choice
                key={entry.id}
                type="button"
                className="organization-option"
                aria-pressed={value === entry.id}
                disabled={disabled || create.isPending}
                onClick={() => {
                  onChange(entry.id);
                  setQuery('');
                  close();
                }}
              >
                <span>{entry.path}</span>
                {value === entry.id ? <Check size={15} aria-hidden="true" /> : null}
              </button>
            ))}
          </div>
          {!filtered.length && query.trim() ? (
            <p className="organization-hint">没有匹配的文件夹</p>
          ) : null}
          {allowCreate &&
          query.trim() &&
          query.trim().length <= 100 &&
          !all.some(
            (entry) =>
              entry.parentId === null &&
              entry.name.toLocaleLowerCase() === query.trim().toLocaleLowerCase(),
          ) ? (
            <Button
              className="organization-create"
              busy={create.isPending}
              disabled={disabled}
              onClick={() => create.mutate()}
            >
              <Plus size={14} />
              在顶层创建“{query.trim()}”
            </Button>
          ) : null}
          <ErrorNotice error={create.error} />
        </div>
      ) : null}
    </div>
  );
}

export function TagPicker({
  tags,
  value,
  onChange,
  disabled = false,
  allowCreate = true,
  onBusyChange,
}: {
  tags: Tag[];
  value: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  allowCreate?: boolean;
  onBusyChange?: (busy: boolean) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState('');
  const [created, setCreated] = useState<Tag[]>([]);
  const trigger = useRef<HTMLButtonElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const focusCreated = useRef(false);
  const id = useId();
  const client = useQueryClient();
  const all = mergeCreated(tags, created);
  const filtered = all.filter((entry) =>
    entry.name.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()),
  );
  const create = useMutation({
    mutationFn: () => api.createTag(query.trim()),
    onSuccess: (tag) => {
      setCreated((entries) => [...entries, tag]);
      client.setQueryData<{ items: Tag[] }>(['tags'], (previous) => ({
        items: mergeCreated(previous?.items || tags, [tag]),
      }));
      void client.invalidateQueries({ queryKey: ['tags'] });
      onChange([...new Set([...value, tag.id])]);
      setQuery('');
      focusCreated.current = true;
    },
  });
  useEffect(() => {
    if (expanded) search.current?.focus();
  }, [expanded]);
  useEffect(() => {
    if (!create.isPending && focusCreated.current) {
      focusCreated.current = false;
      search.current?.focus();
    }
  }, [create.isPending]);
  useEffect(() => {
    onBusyChange?.(create.isPending);
  }, [create.isPending, onBusyChange]);
  return (
    <div className="organization-picker">
      <span className="field-label" id={`${id}-label`}>
        标签
      </span>
      <div className="organization-tag-summary">
        {all
          .filter((tag) => value.includes(tag.id))
          .map((tag) => (
            <button
              type="button"
              className="organization-tag"
              key={tag.id}
              disabled={disabled || create.isPending}
              aria-label={`移除标签 ${tag.name}`}
              onClick={() => onChange(value.filter((entry) => entry !== tag.id))}
            >
              {tag.name}
              <X size={12} aria-hidden="true" />
            </button>
          ))}
        <button
          type="button"
          ref={trigger}
          className="organization-tag-add"
          aria-expanded={expanded}
          aria-controls={`${id}-panel`}
          disabled={disabled || create.isPending}
          onClick={() => setExpanded((previous) => !previous)}
        >
          <TagIcon size={14} aria-hidden="true" />
          {expanded ? '收起标签' : value.length ? '编辑标签' : '添加标签'}
        </button>
      </div>
      {expanded ? (
        <div
          id={`${id}-panel`}
          className="organization-panel"
          aria-labelledby={`${id}-label`}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              event.stopPropagation();
              setExpanded(false);
              trigger.current?.focus();
            } else moveChoice(event);
          }}
        >
          <div className="organization-search">
            <MagnifyingGlass size={15} aria-hidden="true" />
            <Input
              ref={search}
              type="search"
              aria-label="搜索标签"
              placeholder={allowCreate ? '搜索或创建标签…' : '搜索标签…'}
              value={query}
              disabled={disabled || create.isPending}
              onChange={(event) => {
                setQuery(event.target.value);
                create.reset();
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') event.preventDefault();
              }}
            />
          </div>
          <div className="organization-options">
            {filtered.map((tag) => (
              <label
                key={tag.id}
                className="organization-option"
                data-selected={value.includes(tag.id)}
              >
                <Input
                  data-picker-choice
                  type="checkbox"
                  checked={value.includes(tag.id)}
                  disabled={
                    disabled || create.isPending || (!value.includes(tag.id) && value.length >= 50)
                  }
                  onChange={(event) =>
                    onChange(
                      event.target.checked
                        ? [...value, tag.id]
                        : value.filter((entry) => entry !== tag.id),
                    )
                  }
                />
                <span>{tag.name}</span>
              </label>
            ))}
          </div>
          {!filtered.length ? (
            <p className="organization-hint">
              {query.trim() ? '没有匹配的标签' : '还没有标签，输入名称即可创建。'}
            </p>
          ) : null}
          {allowCreate &&
          value.length < 50 &&
          query.trim() &&
          query.trim().length <= 50 &&
          !all.some(
            (entry) => entry.name.toLocaleLowerCase() === query.trim().toLocaleLowerCase(),
          ) ? (
            <Button
              className="organization-create"
              busy={create.isPending}
              disabled={disabled}
              onClick={() => create.mutate()}
            >
              <Plus size={14} />
              创建“{query.trim()}”并选中
            </Button>
          ) : null}
          {value.length >= 50 ? (
            <p className="organization-hint">每条内容最多选择 50 个标签。</p>
          ) : null}
          <ErrorNotice error={create.error} />
        </div>
      ) : null}
    </div>
  );
}

export function OrganizationPicker({
  folders,
  tags,
  folderId,
  tagIds,
  onFolderChange,
  onTagsChange,
  disabled,
  onBusyChange,
}: {
  folders: Folder[];
  tags: Tag[];
  folderId: string;
  tagIds: string[];
  onFolderChange: (id: string) => void;
  onTagsChange: (ids: string[]) => void;
  disabled?: boolean;
  onBusyChange?: (busy: boolean) => void;
}) {
  const [folderBusy, setFolderBusy] = useState(false);
  const [tagBusy, setTagBusy] = useState(false);
  useEffect(() => {
    onBusyChange?.(folderBusy || tagBusy);
  }, [folderBusy, tagBusy, onBusyChange]);
  return (
    <div className="organization-fields">
      <FolderPicker
        folders={folders}
        value={folderId}
        onChange={onFolderChange}
        label="保存到"
        disabled={disabled || tagBusy}
        onBusyChange={setFolderBusy}
      />
      <TagPicker
        tags={tags}
        value={tagIds}
        onChange={onTagsChange}
        disabled={disabled || folderBusy}
        onBusyChange={setTagBusy}
      />
    </div>
  );
}
